import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Link2, Search, Unlink, User, X } from "lucide-react";
import { searchCrmProjects } from "services/project-crm-link.service";
import { CrmProjectLinkSummary } from "types/Desk";
import { REVEAL_ON_HOVER_CLS } from "components/desk-row-actions/reveal-on-hover";

const PANEL_WIDTH = 384;
const PANEL_Z = 80;
const VIEW_PAD = 8;

/**
 * The part of the screen the person can actually see. iOS keeps window.innerHeight
 * when the keyboard opens; the visual viewport is what shrinks.
 */
export function visibleArea(): { top: number; bottom: number } {
  const viewport = window.visualViewport;
  if (!viewport) return { top: 0, bottom: window.innerHeight };
  return { top: viewport.offsetTop, bottom: viewport.offsetTop + viewport.height };
}

function formatValueEur(value: number | null): string {
  if (value === null || value === undefined) return "";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);
}

function MiniStageBar({
  columnOrder,
  currentColumnId,
}: {
  columnOrder: string[];
  currentColumnId: string;
}) {
  if (!columnOrder.length) return null;
  const currentIndex = Math.max(0, columnOrder.indexOf(currentColumnId));
  return (
    <div className="flex h-1.5 gap-0.5">
      {columnOrder.map((colId, index) => (
        <div
          key={colId}
          className={`flex-1 rounded-sm ${
            index <= currentIndex
              ? "bg-emerald-500"
              : "bg-zinc-200 dark:bg-zinc-700"
          }`}
        />
      ))}
    </div>
  );
}

function OutcomeBadge({
  outcome,
  label,
}: {
  outcome: CrmProjectLinkSummary["outcome"];
  label: string;
}) {
  if (!outcome) return null;
  const colors: Record<string, string> = {
    WON: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300",
    LOST: "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300",
    DELETED: "bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
  };
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${colors[outcome] || colors.DELETED}`}
    >
      {label}
    </span>
  );
}

type ProjectCardProps = {
  entry: CrmProjectLinkSummary;
  companyId: string;
  linked?: boolean;
  linking?: boolean;
  disabled?: boolean;
  onLink?: () => void;
  onUnlink?: () => void;
  outcomeLabel: (o: CrmProjectLinkSummary["outcome"]) => string;
};

function ProjectLinkCard({
  entry,
  companyId,
  linked,
  linking,
  disabled,
  onLink,
  onUnlink,
  outcomeLabel,
}: ProjectCardProps) {
  const value = formatValueEur(entry.valueEur);
  const detailUrl =
    entry.boardId &&
    `/app/${companyId}/project-boards/${entry.boardId}/project/${entry.id}`;

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-3 shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300">
          <span className="text-sm font-bold">€</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {detailUrl ? (
              <Link
                to={detailUrl}
                className="truncate text-sm font-semibold text-zinc-900 hover:text-indigo-600 dark:text-zinc-100"
              >
                {entry.name}
              </Link>
            ) : (
              <span className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                {entry.name}
              </span>
            )}
            <OutcomeBadge outcome={entry.outcome} label={outcomeLabel(entry.outcome)} />
          </div>
          {value && (
            <div className="mt-0.5 text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {value}
            </div>
          )}
          {entry.user?.name && (
            <div className="mt-1 flex items-center gap-1 text-xs text-zinc-500">
              <User className="h-3 w-3 shrink-0" />
              <span className="truncate">{entry.user.name}</span>
            </div>
          )}
          {entry.boardName && (
            <div className="mt-0.5 truncate text-[11px] text-zinc-400">
              {entry.boardName}
              {entry.columnName ? ` · ${entry.columnName}` : ""}
            </div>
          )}
          <div className="mt-2">
            <MiniStageBar
              columnOrder={entry.columnOrder}
              currentColumnId={entry.projectBoardColumn}
            />
          </div>
        </div>
        <div className="shrink-0">
          {linked ? (
            <button
              type="button"
              disabled={disabled || linking}
              onClick={onUnlink}
              title="Unlink"
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-zinc-200 text-zinc-500 max-md:h-11 max-md:w-11 hover:border-red-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 dark:border-zinc-700 dark:hover:border-red-800 dark:hover:bg-red-950/30"
            >
              <Unlink className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              disabled={disabled || linking}
              onClick={onLink}
              title="Link"
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-40 max-md:h-11 max-md:w-11"
            >
              <Link2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

type AnchorRect = { top: number; bottom: number; left: number; right: number };

type Props = {
  companyId: string;
  linkedProject: CrmProjectLinkSummary | null;
  disabled?: boolean;
  /** full = labeled button (detail header); row = list hover control */
  variant?: "full" | "row";
  onLink: (projectId: string) => Promise<void>;
  onUnlink: () => Promise<void>;
};

export default function DeskLinkCrmProjectPopover({
  companyId,
  linkedProject,
  disabled,
  variant = "full",
  onLink,
  onUnlink,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CrmProjectLinkSummary[]>([]);
  const [searching, setSearching] = useState(false);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  const [ready, setReady] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<number | null>(null);

  const outcomeLabel = useCallback(
    (outcome: CrmProjectLinkSummary["outcome"]) => {
      if (!outcome) return "";
      return t(`project_outcome_${outcome.toLowerCase()}`, {
        defaultValue: outcome,
      });
    },
    [t]
  );

  const readTriggerAnchor = useCallback((): AnchorRect | null => {
    const el = triggerRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) return null;
    return { top: r.top, bottom: r.bottom, left: r.left, right: r.right };
  }, []);

  const applyPanelPosition = useCallback((trigger: AnchorRect) => {
    const panel = panelRef.current;
    if (!panel) return false;

    const width = Math.min(PANEL_WIDTH, window.innerWidth - VIEW_PAD * 2);
    const area = visibleArea();
    // Capped to what is visible, so a long result list scrolls instead of
    // running under the keyboard.
    panel.style.maxHeight = `${Math.max(0, area.bottom - area.top - VIEW_PAD * 2)}px`;
    const panelH = panel.offsetHeight || 280;

    let left = trigger.right - width;
    left = Math.min(
      Math.max(VIEW_PAD, left),
      window.innerWidth - width - VIEW_PAD
    );

    const minTop = area.top + VIEW_PAD;
    const maxTop = area.bottom - panelH - VIEW_PAD;
    let top = trigger.bottom + VIEW_PAD;
    if (top > maxTop) {
      const above = trigger.top - panelH - VIEW_PAD;
      if (above >= minTop) top = above;
    }
    // The trigger can sit above or below what is visible (a scrolled visual
    // viewport), so the panel is always pulled back into the visible area.
    top = Math.max(minTop, Math.min(top, maxTop));

    panel.style.position = "fixed";
    panel.style.top = `${Math.round(top)}px`;
    panel.style.left = `${Math.round(left)}px`;
    panel.style.width = `${Math.round(width)}px`;
    panel.style.zIndex = String(PANEL_Z);
    panel.style.visibility = "visible";
    return true;
  }, []);

  const openFromEvent = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (open) {
      setOpen(false);
      setAnchor(null);
      setReady(false);
      return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    setAnchor({
      top: r.top,
      bottom: r.bottom,
      left: r.left,
      right: r.right,
    });
    setReady(false);
    setOpen(true);
  };

  useLayoutEffect(() => {
    if (!open) {
      setReady(false);
      return;
    }
    const trigger = anchor || readTriggerAnchor();
    if (!trigger) return;

    let cancelled = false;
    let didFocus = false;
    const run = () => {
      if (cancelled) return;
      if (applyPanelPosition(trigger)) {
        setReady(true);
        if (!didFocus) {
          didFocus = true;
          // Focus after position so the browser doesn't scroll the page to (0,0).
          window.requestAnimationFrame(() => {
            searchInputRef.current?.focus({ preventScroll: true });
          });
        }
      }
    };

    // Panel mounts this commit; measure after layout, then once more for size settle.
    run();
    const raf = window.requestAnimationFrame(run);

    const onReposition = () => {
      const next = readTriggerAnchor() || trigger;
      applyPanelPosition(next);
    };
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    // The on-screen keyboard resizes only the visual viewport.
    window.visualViewport?.addEventListener("resize", onReposition);
    window.visualViewport?.addEventListener("scroll", onReposition);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
      window.visualViewport?.removeEventListener("resize", onReposition);
      window.visualViewport?.removeEventListener("scroll", onReposition);
    };
  }, [open, anchor, applyPanelPosition, readTriggerAnchor, results.length, linkedProject, searching, query]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
      setAnchor(null);
      setReady(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }
    const q = query.trim();
    if (!q) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = window.setTimeout(() => {
      searchCrmProjects(companyId, q, 20)
        .then((data) => {
          setResults(Array.isArray(data.entries) ? data.entries : []);
        })
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, [companyId, open, query]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  const handleLink = async (entry: CrmProjectLinkSummary) => {
    setLinkingId(entry.id);
    try {
      await onLink(entry.id);
      setOpen(false);
      setAnchor(null);
      setReady(false);
    } finally {
      setLinkingId(null);
    }
  };

  const handleUnlink = async () => {
    setLinkingId(linkedProject?.id || "unlink");
    try {
      await onUnlink();
    } finally {
      setLinkingId(null);
    }
  };

  const triggerLabel = t("desk_link_crm_project", { defaultValue: "Link item" });

  const triggerButton =
    variant === "row" ? (
      linkedProject ? (
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          onClick={openFromEvent}
          title={linkedProject.name}
          className="inline-flex max-w-[9rem] items-center gap-1 rounded-md bg-lime-50 px-1.5 py-1 text-[11px] font-semibold text-lime-800 hover:bg-lime-100 disabled:opacity-40 dark:bg-lime-950/40 dark:text-lime-300 dark:hover:bg-lime-950/60"
        >
          <Link2 className="h-3 w-3 shrink-0" />
          <span className="truncate">{linkedProject.name}</span>
        </button>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          onClick={openFromEvent}
          title={triggerLabel}
          aria-label={triggerLabel}
          className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-zinc-400 hover:bg-indigo-50 hover:text-indigo-600 dark:hover:bg-indigo-950/30 dark:hover:text-indigo-400 transition-opacity ${
            open ? "opacity-100" : REVEAL_ON_HOVER_CLS
          }`}
        >
          <Link2 className="h-4 w-4" />
        </button>
      )
    ) : (
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={openFromEvent}
        className="flex h-8 items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800 max-md:h-11 max-md:px-3.5 max-md:text-sm"
      >
        <Link2 className="h-3.5 w-3.5" />
        {triggerLabel}
      </button>
    );

  const panel =
    open &&
    createPortal(
      <div
        ref={panelRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: PANEL_Z,
          width: Math.min(PANEL_WIDTH, window.innerWidth - VIEW_PAD * 2),
          visibility: ready ? "visible" : "hidden",
          pointerEvents: ready ? "auto" : "none",
        }}
        className="flex flex-col rounded-xl border border-zinc-200 bg-white p-4 shadow-xl dark:border-zinc-700 dark:bg-zinc-900"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
          {t("desk_link_crm_project_title", { defaultValue: "Link" })}
        </p>

        {linkedProject && (
          <div className="mb-3">
            <p className="mb-1.5 text-xs font-medium text-zinc-500">
              {t("desk_linked_crm_project", { defaultValue: "Linked deal" })}
            </p>
            <ProjectLinkCard
              entry={linkedProject}
              companyId={companyId}
              linked
              linking={!!linkingId}
              disabled={disabled}
              onUnlink={handleUnlink}
              outcomeLabel={outcomeLabel}
            />
          </div>
        )}

        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input
            ref={searchInputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("desk_link_crm_project_search", {
              defaultValue: "Search deals…",
            })}
            className="w-full rounded-lg border border-zinc-300 py-2 pl-9 pr-8 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 dark:border-zinc-600 dark:bg-zinc-950 max-md:min-h-11 max-md:text-base"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-400 hover:text-zinc-600 max-md:p-2.5"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="max-h-72 min-h-0 space-y-2 overflow-y-auto">
          {searching && (
            <p className="py-4 text-center text-sm text-zinc-500">
              {t("loading", { defaultValue: "Loading…" })}
            </p>
          )}
          {!searching && query.trim() && results.length === 0 && (
            <p className="py-4 text-center text-sm text-zinc-500">
              {t("desk_link_crm_project_empty", {
                defaultValue: "No deals match your search.",
              })}
            </p>
          )}
          {!searching &&
            results.map((entry) => (
              <ProjectLinkCard
                key={entry.id}
                entry={entry}
                companyId={companyId}
                linked={linkedProject?.id === entry.id}
                linking={linkingId === entry.id}
                disabled={disabled}
                onLink={() => handleLink(entry)}
                onUnlink={handleUnlink}
                outcomeLabel={outcomeLabel}
              />
            ))}
          {!query.trim() && !linkedProject && (
            <p className="py-4 text-center text-sm text-zinc-500">
              {t("desk_link_crm_project_hint", {
                defaultValue: "Type to search pipeline deals.",
              })}
            </p>
          )}
        </div>
      </div>,
      document.body
    );

  return (
    <div className="relative inline-flex" ref={rootRef}>
      {triggerButton}
      {panel}
    </div>
  );
}
